import { useQuery } from '@tanstack/react-query';
import { HardDrive, Cloud, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { importService, ImportSource } from '@/services/api/import';
import { cn } from '@/lib/utils';

interface SourceOption {
  id: ImportSource;
  label: string;
  hint: string;
  brandColor: string;
  needsAuth: boolean;
}

const SOURCES: SourceOption[] = [
  { id: 'local',    label: 'Local files',  hint: 'Drag-drop from this computer',     brandColor: '#52525b', needsAuth: false },
  { id: 'gdrive',   label: 'Google Drive', hint: 'Browse your Google Drive folders', brandColor: '#0F9D58', needsAuth: true  },
  { id: 'onedrive', label: 'OneDrive',     hint: 'Browse your OneDrive folders',     brandColor: '#0078D4', needsAuth: true  },
  { id: 'dropbox',  label: 'Dropbox',      hint: 'Browse your Dropbox folders',      brandColor: '#0061FF', needsAuth: true  },
];

export default function Step1Source({ onPick }: { onPick: (source: ImportSource) => void }) {
  const { data: status } = useQuery({
    queryKey: ['cloud-status'],
    queryFn: () => importService.cloudStatus(),
    staleTime: 30_000,
  });

  const isConnected = (id: ImportSource): boolean => {
    if (id === 'local') return true;
    if (id === 'gdrive') return !!status?.googleDrive?.connected;
    if (id === 'onedrive') return !!status?.oneDrive?.connected;
    if (id === 'dropbox') return !!status?.dropbox?.connected;
    return false;
  };

  const connect = async (id: ImportSource) => {
    let url: string | null = null;
    if (id === 'gdrive') url = await importService.connectGoogleDrive();
    if (id === 'onedrive') url = await importService.connectOneDrive();
    if (id === 'dropbox') url = await importService.connectDropbox();
    if (url) window.location.href = url;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {SOURCES.map((s) => {
        const connected = isConnected(s.id);
        return (
          <Card
            key={s.id}
            className={cn('cursor-pointer transition-all hover:shadow-md', !connected && s.needsAuth && 'opacity-90')}
            onClick={() => {
              if (s.needsAuth && !connected) { void connect(s.id); return; }
              onPick(s.id);
            }}
          >
            <CardContent className="pt-6 pb-5 text-center space-y-3">
              <div
                className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl"
                style={{ backgroundColor: `${s.brandColor}1a`, color: s.brandColor }}
              >
                {s.id === 'local' ? <HardDrive className="h-6 w-6" /> : <Cloud className="h-6 w-6" />}
              </div>
              <div>
                <p className="font-semibold text-sm">{s.label}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{s.hint}</p>
              </div>
              {s.needsAuth && (
                connected ? (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Connected
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">Connect</Badge>
                )
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
